import { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { FirstAidKit, IdentificationCard } from '@phosphor-icons/react';
import { ScanIDDialog } from './ScanIDDialog';

export function PatientInfoSection({ formData, handleInputChange, patientId }) {
  const [showScanDialog, setShowScanDialog] = useState(false);

  const handleScanData = (data, scannedImages) => {
    if (data.first_name) handleInputChange('first_name', data.first_name);
    if (data.last_name) handleInputChange('last_name', data.last_name);
    if (data.dob) handleInputChange('dob', data.dob);
    if (data.gender) handleInputChange('gender', data.gender);
    if (data.address) handleInputChange('address', data.address);

    // Store scanned images reference in form data for later attachment
    if (scannedImages?.length > 0) {
      handleInputChange('_scanned_id_images', scannedImages);
    }
  };

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center gap-2">
              <FirstAidKit size={24} className="text-blue-600" />
              Patient Information
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => setShowScanDialog(true)}
              data-testid="scan-id-button"
            >
              <IdentificationCard size={18} className="mr-2" />
              Scan ID
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                First Name *
              </Label>
              <Input
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="First Name"
                className="rounded-sm border-slate-300"
                data-testid="patient-firstname-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Last Name *
              </Label>
              <Input
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Last Name"
                className="rounded-sm border-slate-300"
                data-testid="patient-lastname-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Date of Birth
              </Label>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                className="rounded-sm border-slate-300"
                data-testid="patient-dob-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Gender
              </Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => handleInputChange('gender', value)}
              >
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="patient-gender-select">
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="X">X</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Address
              </Label>
              <Input
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Street Address, City, State, ZIP"
                className="rounded-sm border-slate-300"
                data-testid="patient-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Telephone
              </Label>
              <Input
                type="tel"
                value={formData.tel}
                onChange={(e) => handleInputChange('tel', e.target.value)}
                placeholder="(XXX) XXX-XXXX"
                className="rounded-sm border-slate-300"
                data-testid="patient-tel-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <ScanIDDialog
        open={showScanDialog}
        onClose={() => setShowScanDialog(false)}
        onDataExtracted={handleScanData}
      />
    </>
  );
}
